import { useEffect, useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { db, type RecursoTipoLocal, type ServicioTramoPersonaLocal } from '../datos/db.js'
import { sincronizar } from '../datos/sync.js'
import { Boton } from '../comunes/Boton.js'

/**
 * Duración y limpieza propias de un profesional, por servicio y por tramo (T32c2).
 *
 * El catálogo dice cuánto dura un servicio. Lo que se edita acá es **la variante
 * de esta persona**: la doctora que necesita más tiempo entre un paciente y el
 * siguiente, el que corta antes. Son los mismos dos números que ya existen, con
 * dos fuentes —catálogo y propio— y el motor usa el propio cuando está.
 *
 * **Lee todo de Dexie** (§5.5). Servicios, tramos, requisitos, tipos y lo ya
 * cargado por esta persona salen del canal operativo; lo único que va por la red
 * es el `PUT` de guardar, que es exactamente el mismo camino que el resto de
 * `Configuracion.tsx` (configuración no es trabajo de agenda: se edita con
 * conexión y desde el escritorio de quien administra).
 *
 * Un detalle que no es obvio al leer la base: **la limpieza no está en el
 * tramo.** El tramo dice cuánto dura; el tiempo de limpieza es el `buffer_min`
 * del *tipo de espacio* que ese tramo ocupa, así que se resuelve cruzando
 * `servicio_tramo_req` con `recurso_tipo`. Si hay más de un espacio se toma el
 * mayor. Y si el tramo no pide ningún espacio —una consulta que se hace en el
 * sillón— no hay limpieza que cambiar: la fila no ofrece el campo y manda
 * `bufferMin: null`.
 */

const API = '/api'

/** Un tramo de un servicio activo, con los dos números de catálogo resueltos. */
interface Fila {
  tramoId: string
  orden: number
  /** Un servicio de un solo tramo no dice "tramo 1": no hay a cuál elegir. */
  unTramoSolo: boolean
  servicioNombre: string
  duracionCatalogo: number
  /** `null` = el tramo no ocupa ningún espacio: no hay limpieza que cambiar. */
  limpiezaCatalogo: number | null
  propia: ServicioTramoPersonaLocal | null
}

export interface DuracionesProfesionalProps {
  personaId: string
}

export function DuracionesProfesional({ personaId }: DuracionesProfesionalProps) {
  const filas = useLiveQuery(() => filasDe(personaId), [personaId], null)

  return (
    <section className="mt-4 border-t border-slate-100 pt-4">
      <h3 className="text-sm font-semibold text-slate-900">Duración y limpieza por servicio</h3>
      <p className="txt-secundario mb-3">
        Sólo para este profesional. Vacío = se usa el del catálogo.
      </p>

      {filas === null ? (
        <p className="text-sm text-slate-400">Leyendo…</p>
      ) : filas.length === 0 ? (
        <p className="text-sm text-slate-500">
          No hay servicios activos. Cuando cargues uno, aparece acá.
        </p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {filas.map((f) => (
            <FilaTramo key={f.tramoId} fila={f} personaId={personaId} />
          ))}
        </ul>
      )}

      <p className="mt-2 text-xs text-slate-500">
        El cambio vale para turnos nuevos: las citas ya agendadas conservan su duración.
      </p>
    </section>
  )
}

/** Un tramo con sus dos campos y su propio botón de guardar. */
function FilaTramo({ fila, personaId }: { fila: Fila; personaId: string }) {
  const propiaDuracion = fila.propia?.duracion_min ?? null
  const propiaLimpieza = fila.propia?.buffer_min ?? null
  const conEspacio = fila.limpiezaCatalogo !== null

  const [duracion, setDuracion] = useState(propiaDuracion === null ? '' : String(propiaDuracion))
  const [limpieza, setLimpieza] = useState(propiaLimpieza === null ? '' : String(propiaLimpieza))
  const [guardado, setGuardado] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [guardando, setGuardando] = useState(false)

  // El valor de Dexie manda, y sólo cambia el input cuando *él* cambia: si se
  // re-sincronizara en cada render, escribir un dígito se pelearía con el
  // guardado. Se dispara sólo cuando el pull trae otra cosa de la que había.
  useEffect(() => {
    setDuracion(propiaDuracion === null ? '' : String(propiaDuracion))
    setLimpieza(propiaLimpieza === null ? '' : String(propiaLimpieza))
  }, [propiaDuracion, propiaLimpieza])

  const hayPropio =
    propiaDuracion !== null || (conEspacio && propiaLimpieza !== null)

  // Con los dos campos vacíos, guardar es borrar lo que esta persona tenía
  // cargado. Decirlo evita el "guardé y no pasó nada".
  const vacio = duracion === '' && (!conEspacio || limpieza === '')

  async function guardar() {
    // Mismos límites que la API (`PUT /config/duraciones-persona`): enteros,
    // duración 1–1440 y limpieza 0–1440. Así el error se ve antes de ir a la red.
    const invalido =
      fueraDeRango(duracion, 1) ? 'La duración tiene que ser un número entero de 1 a 1440 minutos' :
      conEspacio && fueraDeRango(limpieza, 0) ? 'La limpieza tiene que ser un número entero de 0 a 1440 minutos' :
      null
    if (invalido) {
      setGuardado(false)
      setError(invalido)
      return
    }
    setGuardando(true)
    setGuardado(false)
    setError(null)
    try {
      const res = await fetch(`${API}/config/duraciones-persona`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          servicioTramoId: fila.tramoId,
          personaId,
          duracionMin: aMinutos(duracion),
          // Sin espacio en el tramo no hay limpieza que mandar: null y listo.
          bufferMin: conEspacio ? aMinutos(limpieza) : null,
        }),
      })

      if (!res.ok) {
        const detalle = (await res.json().catch(() => ({}))) as { mensaje?: string }
        setError(detalle.mensaje ?? 'No pudimos guardar el cambio')
        return
      }

      setGuardado(true)
      // El cambio ya está en el servidor. Que la fila se vea guardada no
      // depende de que el pull termine: Dexie se actualiza cuando Corra, y si
      // falla, lo muestra el indicador global de sync, no esta fila.
      void sincronizar()
    } catch {
      setError('No pudimos guardar el cambio')
    } finally {
      setGuardando(false)
    }
  }

  return (
    <li className="py-3">
      <div className="flex flex-wrap items-end gap-3">
        <span className="min-w-0 flex-1 text-[14px] font-medium text-slate-900">
          {fila.servicioNombre}
          {!fila.unTramoSolo && ` · tramo ${fila.orden}`}
        </span>

        <label className="text-sm text-slate-700">
          Duración (min)
          <input
            type="number"
            min={1}
            max={1440}
            step={1}
            value={duracion}
            placeholder={String(fila.duracionCatalogo)}
            onChange={(e) => {
              setDuracion(e.target.value)
              setGuardado(false)
            }}
            className="mt-1 h-9 w-24 rounded-lg border border-slate-300 bg-white px-2 text-[14px]"
          />
        </label>

        {conEspacio ? (
          <label className="text-sm text-slate-700">
            Limpieza (min)
            <input
              type="number"
              min={0}
              max={1440}
              step={1}
              value={limpieza}
              placeholder={String(fila.limpiezaCatalogo)}
              onChange={(e) => {
                setLimpieza(e.target.value)
                setGuardado(false)
              }}
              className="mt-1 h-9 w-24 rounded-lg border border-slate-300 bg-white px-2 text-[14px]"
            />
          </label>
        ) : (
          <span className="pb-2 text-[13px] text-slate-500">sin consultorio</span>
        )}

        <Boton
          variante="primary"
          tamano="sm"
          onClick={() => void guardar()}
          disabled={guardando}
        >
          {guardando ? 'Guardando…' : hayPropio && vacio ? 'Quitar' : 'Guardar'}
        </Boton>
      </div>

      <div className="mt-1 flex flex-wrap items-center gap-3">
        {hayPropio && (
          <button
            type="button"
            onClick={() => {
              setDuracion('')
              setLimpieza('')
              setGuardado(false)
            }}
            className="text-[13px] text-teal-700"
          >
            usar el de catálogo
          </button>
        )}

        {guardado && <span className="text-[13px] text-teal-700">Guardado</span>}
        {error && (
          <span role="alert" className="text-[13px] text-rose-700">
            {error}
          </span>
        )}
      </div>
    </li>
  )
}

/** Vacío es `null`: es lo que el servidor lee como "usá el de catálogo". */
/** Vacío vale (= catálogo); si hay algo, tiene que ser entero entre `min` y 1440. */
function fueraDeRango(texto: string, min: number): boolean {
  if (texto.trim() === '') return false
  const n = Number(texto)
  return !Number.isInteger(n) || n < min || n > 1440
}

function aMinutos(texto: string): number | null {
  return texto.trim() === '' ? null : Number(texto)
}

/** Las filas del componente, con los dos números de catálogo ya resueltos. */
async function filasDe(personaId: string): Promise<Fila[]> {
  const servicios = await db.servicio.filter((s) => s.activo).toArray()
  if (servicios.length === 0) return []

  const nombreServicio = new Map(servicios.map((s) => [s.id, s.nombre]))
  const tramos = await db.servicio_tramo
    .where('servicio_id')
    .anyOf(servicios.map((s) => s.id))
    .sortBy('orden')
  if (tramos.length === 0) return []

  const cuantosPorServicio = new Map<string, number>()
  for (const t of tramos) {
    cuantosPorServicio.set(t.servicio_id, (cuantosPorServicio.get(t.servicio_id) ?? 0) + 1)
  }

  // La limpieza de catálogo es el `buffer_min` del tipo de espacio que el tramo
  // ocupa, no un campo del tramo. Se ignora el requisito de la propia persona:
  // su buffer es justamente lo que se puede cambiar acá.
  const requisitos = await db.servicio_tramo_req
    .where('servicio_tramo_id')
    .anyOf(tramos.map((t) => t.id))
    .toArray()
  const tipos = await db.recurso_tipo.bulkGet([
    ...new Set(requisitos.map((r) => r.recurso_tipo_id)),
  ])
  const tipoPorId = new Map<string, RecursoTipoLocal>()
  for (const t of tipos) if (t) tipoPorId.set(t.id, t)

  const limpiezaPorTramo = new Map<string, number>()
  for (const r of requisitos) {
    const tipo = tipoPorId.get(r.recurso_tipo_id)
    if (!tipo || tipo.es_persona) continue
    limpiezaPorTramo.set(
      r.servicio_tramo_id,
      Math.max(limpiezaPorTramo.get(r.servicio_tramo_id) ?? 0, tipo.buffer_min),
    )
  }

  const propias = await db.servicio_tramo_persona.where('persona_id').equals(personaId).toArray()
  const propiaPorTramo = new Map(propias.map((p) => [p.servicio_tramo_id, p]))

  return tramos
    .map((t) => ({
      tramoId: t.id,
      orden: t.orden,
      unTramoSolo: (cuantosPorServicio.get(t.servicio_id) ?? 0) < 2,
      servicioNombre: nombreServicio.get(t.servicio_id) ?? '',
      duracionCatalogo: t.duracion_min,
      limpiezaCatalogo: limpiezaPorTramo.get(t.id) ?? null,
      propia: propiaPorTramo.get(t.id) ?? null,
    }))
    .sort((a, b) => a.servicioNombre.localeCompare(b.servicioNombre, 'es-PY') || a.orden - b.orden)
}